'use client';

import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { BaseLineClose } from '@/app/components/icons/BaseLineClose';
import { MODEL_VERSION, SITE } from '@/app/constants';
import { useStore } from '@/app/providers/store';
import { cn, reportEvent } from '@/app/utils';
import { useRouter } from '@/i18n/routing';

const ASSET_ORIGIN = SITE === 'china' ? 'https://image01-aliyun.vidu.zone' : 'https://image01.cf.vidu.studio';
const BANNER_ASSETS = {
  bg: `${ASSET_ORIGIN}/vidu/media-asset/banner-bg-5c3500d5.webp`,
  illustration: `${ASSET_ORIGIN}/vidu/media-asset/banner-illustration-2a0d493f.webp`,
};

export const GptImage25Banner = ({ onClose, className }: { onClose: () => void; className?: string }) => {
  const t = useTranslations('gptImage25TopBanner');
  const router = useRouter();
  const updateModelVersion = useStore((state) => state.updateModelVersion);

  const goReference2Image = (elementName: string) => {
    reportEvent('element_click', { element_name: elementName });
    updateModelVersion('reference2image', MODEL_VERSION.GPT_IMAGE_2_5_FAST);
    router.push('/create/reference2image');
  };

  const handleClose = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    reportEvent('element_click', { element_name: 'gpt_image_2_5_top_banner_close' });
    onClose();
  };

  const handleCtaClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    goReference2Image('gpt_image_2_5_top_banner_cta');
  };

  return (
    <div
      className={cn(
        'relative isolate flex min-h-[41px] w-full cursor-pointer items-center justify-between overflow-hidden bg-[#2F3353] px-4',
        className,
      )}
      onClick={() => goReference2Image('gpt_image_2_5_top_banner_main')}
    >
      <div className="pointer-events-none absolute inset-0">
        <Image src={BANNER_ASSETS.bg} alt="" fill className="h-full w-full object-cover" priority />
      </div>

      <div className="relative z-10 flex h-full min-w-0 flex-1 items-center justify-center gap-3 overflow-hidden">
        <div className="flex h-full min-w-0 items-center gap-2 overflow-hidden whitespace-nowrap text-white text-xs">
          <Image
            src={BANNER_ASSETS.illustration}
            alt=""
            width={82}
            height={36}
            className="mr-1 shrink-0 self-end sm:hidden"
            loading="lazy"
          />
          <span className="min-w-0 truncate font-semibold">{t(SITE === 'china' ? 'titleChina' : 'title')}</span>
          <span className="shrink-0 rounded-[4px] bg-[#5C57F2] px-1 py-[1px] font-semibold text-[10px] text-white leading-[1.4] sm:hidden">
            {t('highlight')}
          </span>
        </div>

        <div className="h-[14px] w-px shrink-0 bg-system-white16 sm:hidden" />

        <button
          type="button"
          onClick={handleCtaClick}
          className="shrink-0 cursor-pointer rounded-[8px] border-0 bg-black px-3 py-1 font-semibold text-[10px] text-white leading-[1.4] transition-colors hover:bg-black/80 sm:hidden"
        >
          {t('cta')}
        </button>
      </div>

      <button
        type="button"
        onClick={handleClose}
        className="relative z-10 ml-3 shrink-0 text-white transition-colors hover:text-white/80"
        aria-label={t('close')}
      >
        <BaseLineClose className="size-4" />
      </button>
    </div>
  );
};
